import { IStore } from '@app/redux'
// Adjust this import to whatever you use in your app to access Firebase auth
// (You referenced `auth.currentUser.getIdToken()` in your snippet)
import { auth } from '@app/services'
import Axios from 'axios'
import React from 'react'
import { useSelector } from 'react-redux'

import { TideIcon } from '~public/icons/TideIcon'

import { ToSContent } from './TOS'

type TideModalProps = {
  open: boolean
  toggleOpen: () => void
}

// TODO: Replace this with your real ToS content (or render a component)
export const TOS_TEXT = `
COAST TERMS OF SERVICE
Effective Date: January 1, 2026

Welcome to Coast.

These Terms of Service ("Terms") constitute a legally binding software rental agreement ("Agreement") between you ("User", "you", or "your") and Coast Software (Nevada) ("Coast", "we", "us", or "our").

By accessing, downloading, installing, or using the Coast software platform and related services (the "Services") in any manner, you agree to be bound by these Terms. If you do not agree, you must not access or use the Services.

----------------------------------------------------------------

IMPORTANT DISCLOSURES

• This Agreement is strictly for the rental of software and private keys.
• Coast does NOT provide financial services, asset custody, or fiat onramps.
• Participation is for protocol utility purposes only.
• Bridged assets on PulseChain are NOT redeemable for fiat or original assets.
• Bridged assets may become illiquid or valueless if the PulseChain Bridge fails.

----------------------------------------------------------------

1. DESCRIPTION OF SERVICES

The Services consist of a software platform provided on a rental basis, enabling you to temporarily rent private keys for the purpose of executing your own on-chain transactions on blockchain networks, including swaps of bridged assets on PulseChain.

This is exclusively a software and private key rental arrangement.

Under this Agreement:

• You are solely responsible for initiating and authorizing all transactions.
• Coast does not execute, approve, or monitor transactions.
• Coast provides no fiat currency services.
• Coast has no control over liquidity, pricing, or settlement.
• Coast does not custody digital assets.

All risks associated with blockchain usage are borne entirely by you.

----------------------------------------------------------------

2. ELIGIBILITY AND ACCOUNT REGISTRATION

To use the Services, you must:

• Be at least 18 years old.
• Be legally capable of forming a binding contract.
• Not reside in a prohibited or sanctioned jurisdiction.

You are responsible for maintaining accurate registration information and securing your account credentials. Coast is not liable for unauthorized access resulting from your failure to secure your account.

----------------------------------------------------------------

3. RENTAL AGREEMENT DETAILS

• Rental licenses are temporary, non-exclusive, and revocable.
• Rental periods may be hourly, daily, or monthly.
• Ownership or custody of private keys is never transferred.
• All rental fees are non-refundable unless required by law.
• Account balances are prepaid software rental credits only.
• Balances do not represent stored value or deposits.

Payment processing is handled exclusively by CTS Global (Montana) under its own terms.

----------------------------------------------------------------

4. USER OBLIGATIONS

You agree to:

• Comply with all applicable laws and regulations.
• Independently manage all transactions.
• Accept all risks related to blockchain use.
• Avoid prohibited activities including fraud, abuse, or unauthorized use.

You must NOT:

• Reverse engineer or modify the Services.
• Share rented private keys.
• Use the Services for unlawful or high-risk financial activity.

----------------------------------------------------------------

5. CST KNOWLEDGE TOKEN

CST is a non-redeemable Knowledge Token.

• CST is NOT a stablecoin.
• CST has no monetary value.
• CST cannot be redeemed for cash.
• CST is used solely for protocol utility.

----------------------------------------------------------------

6. COAST LOCK & EARN

Lock & Earn is an optional protocol participation feature.

• It is NOT an investment product.
• It does NOT guarantee returns.
• Participation requires acceptance of separate terms.
• Rewards arise solely from protocol mechanics.

----------------------------------------------------------------

7. DISCLAIMERS AND LIMITATION OF LIABILITY

THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE".

Coast disclaims all liability for:

• Financial losses
• Smart contract failures
• Bridge failures
• Network outages
• Third-party service issues

TOTAL LIABILITY SHALL NOT EXCEED RENTAL FEES PAID IN THE PRIOR 12 MONTHS.

----------------------------------------------------------------

8. INDEMNIFICATION

You agree to indemnify and hold harmless Coast Software (Nevada), its affiliates, officers, employees, and CTS Global (Montana) from all claims arising from your use of the Services.

----------------------------------------------------------------

9. TERMINATION

Coast may suspend or terminate access at any time.

Upon termination:
• Your rental license ends immediately.
• Remaining balances may be forfeited or refunded.
• Legal provisions survive termination.

----------------------------------------------------------------

10. GAME OF SKILL (PLAY FEATURE)

Participation in the Game of Skill:

• Is optional and skill-based
• Requires separate acceptance
• Uses non-refundable entry fees
• Does not guarantee rewards

----------------------------------------------------------------

11. DATA AND PRIVACY

We collect limited data necessary to operate the Services and comply with law. On-chain data is public. Off-chain data is securely stored.

----------------------------------------------------------------

12. INTELLECTUAL PROPERTY

All software and content are owned by Coast or its licensors. You receive a limited personal-use license only.

----------------------------------------------------------------

13. GOVERNING LAW AND DISPUTE RESOLUTION

These Terms are governed by the laws of the State of Montana.

All disputes shall be resolved through binding arbitration in Montana. Class actions are waived.

----------------------------------------------------------------

14. MISCELLANEOUS

• Severability applies.
• These Terms constitute the entire agreement.
• Coast may assign these Terms.
• Force majeure events apply.
• Coast does not engage in money transmission.

----------------------------------------------------------------

CONTACT

For questions, contact: legal@0xcoast.com

BY USING THE SERVICES, YOU CONFIRM THAT YOU HAVE READ, UNDERSTOOD, AND AGREED TO THESE TERMS IN FULL.
`

export const GloryModal: React.FC<TideModalProps> = ({ open, toggleOpen }) => {
  const { user } = useSelector((state: IStore) => state)

  const ToSAccepted = (user as any)?.ToSAccepted
  //const uid = (user as any)?.uid || (user as any)?.id || (user as any)?.userId

  // ---- ToS modal state (BLOCKING) ----
  const mustAcceptToS = ToSAccepted !== true
  const [tosScrolledToBottom, setTosScrolledToBottom] = React.useState(false)
  const [tosSubmitting, setTosSubmitting] = React.useState(false)
  const [tosError, setTosError] = React.useState<string | null>(null)

  const tosScrollRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    // If user changes (or refresh) and ToS becomes accepted, reset ToS UI state.
    if (!mustAcceptToS) {
      setTosScrolledToBottom(false)
      setTosSubmitting(false)
      setTosError(null)
    }
  }, [mustAcceptToS])

  const handleTosScroll = () => {
    const el = tosScrollRef.current
    if (!el) return
    const thresholdPx = 8
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - thresholdPx
    if (atBottom) setTosScrolledToBottom(true)
  }

  const handleAcceptToS = async () => {
    try {
      setTosError(null)
      setTosSubmitting(true)

      //if (!uid) throw new Error('Missing user id (uid).')
      if (!auth?.currentUser) throw new Error('Not signed in.')

      const authorization = await auth.currentUser.getIdToken()
      const data = { case: 'ToS', accepted: true }
      const headers = { Authorization: authorization }

      await Axios.post('/api/north-one/accept-tos', data, { headers })

      // simplest + most reliable "refresh user"
      window.location.reload()
    } catch (e: any) {
      setTosError(e?.message || 'Failed to accept Terms of Service.')
      setTosSubmitting(false)
    }
  }

  // ---- Original promo modal state/logic (UNCHANGED when ToS accepted) ----
  const [shouldShow, setShouldShow] = React.useState(false)

  React.useEffect(() => {
    // Only run promo logic if ToS is already accepted
    if (mustAcceptToS) {
      setShouldShow(false)
      return
    }

    if (!open) {
      setShouldShow(false)
      return
    }

    const skipModal = sessionStorage.getItem('skipTideModal')
    if (skipModal === 'true') {
      setShouldShow(false)
      return
    }

    const key = 'tideModalSingleCount'
    const stored = localStorage.getItem(key)
    const count = stored ? parseInt(stored, 10) || 0 : 0

    if (count >= 2) {
      setShouldShow(false)
      return
    }

    localStorage.setItem(key, String(count + 1))
    setShouldShow(true)
  }, [open, mustAcceptToS])

  const handleOverlayClick = () => {
    // Only close promo modal on overlay click (never close ToS modal)
    if (mustAcceptToS) return
    toggleOpen()
  }

  const handleCtaClick = () => {
    sessionStorage.setItem('skipTideModal', 'true')
    toggleOpen()
  }

  // =========================================================
  // 1) BLOCKING ToS MODAL (rendered whenever not accepted)
  // =========================================================
  if (mustAcceptToS) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
          backdropFilter: 'blur(6px)',
          padding: '15px',
        }}
        // IMPORTANT: no onClick close handler
      >
        <div
          style={{
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0px 10px 28px rgba(0, 0, 0, 0.6)',
            maxWidth: '900px',
            width: '95%',
            position: 'relative',
            border: '2px solid #05D9FF',
            background: 'linear-gradient(to right, #000000, rgb(17, 24, 69), #000000)',
            color: '#fff',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontSize: '24px', fontWeight: 900, marginBottom: '10px' }}>Terms of Service</div>

          <div
            ref={tosScrollRef}
            onScroll={handleTosScroll}
            style={{
              maxHeight: '55vh',
              overflowY: 'auto',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '10px',
              padding: '16px',
              backgroundColor: 'rgba(0,0,0,0.35)',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            <ToSContent popup={true} />
          </div>

          <div style={{ marginTop: '12px', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>
              {tosScrolledToBottom ? 'You can now accept the Terms.' : 'Scroll to the bottom to enable acceptance.'}
            </div>
          </div>

          {tosError ? <div style={{ marginTop: '10px', color: '#ffb3b3', fontSize: '13px' }}>{tosError}</div> : null}

          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleAcceptToS}
              disabled={!tosScrolledToBottom || tosSubmitting}
              style={{
                cursor: !tosScrolledToBottom || tosSubmitting ? 'not-allowed' : 'pointer',
                opacity: !tosScrolledToBottom || tosSubmitting ? 0.6 : 1,
                color: '#fff',
                borderRadius: '8px',
                border: '3px solid #05D9FF',
                fontWeight: 'bold',
                fontSize: '16px',
                padding: '12px 16px',
                background: 'rgba(5, 217, 255, 0.12)',
              }}
            >
              {tosSubmitting ? 'Accepting…' : 'I Accept'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // =========================================================
  // 2) ORIGINAL PROMO MODAL (only if open + shouldShow)
  // =========================================================
  if (!open || !shouldShow) {
    return null
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(5px)',
        padding: '15px',
      }}
      onClick={handleOverlayClick}
    >
      <div
        style={{
          padding: '20px',
          paddingTop: '40px',
          borderRadius: '10px',
          boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.5)',
          textAlign: 'center',
          maxWidth: '500px',
          width: '90%',
          position: 'relative',
          border: '2px solid #05D9FF',
          background: 'linear-gradient(to right, #000000, rgb(17, 24, 69), #000000)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              fontSize: '48px',
              fontWeight: '900',
              color: '#ffffff',
              WebkitTextStroke: '2px #ffffff',
              paddingBottom: '20px',
            }}
          >
            <span>$TIDE</span>
            <span>ON</span>
            <span>PULSECHAIN</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'center' }}>
            <a
              href="https://beta.0xtide.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCtaClick}
              style={{
                color: '#fff',
                borderRadius: '6px',
                border: '4px solid #05D9FF',
                fontWeight: 'bold',
                textDecoration: 'none',
                fontSize: '26px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'inset 0px -3px 5px rgba(0, 0, 0, 0.3)',
                textAlign: 'center',
                lineHeight: '1.2',
                width: '80%',
                gap: '20px',
                paddingTop: '20px',
                paddingBottom: '20px',
                paddingLeft: '5px',
                paddingRight: '5px',
              }}
            >
              <TideIcon style={{ height: '60px', width: '60px' }} />
              <span style={{ paddingTop: '10px', fontSize: '30px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                Learn More
              </span>
            </a>
          </div>
          <p style={{ marginTop: '10px', marginBottom: '0px' }}>
            <span style={{ color: '#ffffff', fontSize: '12px' }}>$TIDE deepens liquidity on PulseChain.</span>
          </p>
        </>
      </div>
    </div>
  )
}
